(() => {
  if (window.RayCartCrossSellInitialized) return;

  window.RayCartCrossSellInitialized = true;

  const selectors = {
    card: '[data-ray-cross-sell-card]',
    form: '[data-ray-cross-sell-form]',
    variantsJson: '[data-ray-cross-sell-variants]',
    optionControl: '[data-ray-cross-sell-option]',
    fallbackSelect: '[data-ray-cross-sell-fallback-select]',
    variantIdInput: '[data-ray-cross-sell-variant-id]',
    price: '[data-ray-cross-sell-price]',
    comparePrice: '[data-ray-cross-sell-compare-price]',
    image: '.ray-cart-cross-sell__image',
    drawerInner: '[data-hydration-key="cart-drawer-inner"]',
    cartIcon: '[data-testid="cart-icon"]',
  };

  const getVariants = (card) => {
    const json = card.querySelector(selectors.variantsJson);

    if (!json) return [];

    try {
      return JSON.parse(json.textContent.trim());
    } catch (error) {
      console.error('Ray cross-sell variants JSON parse failed', error);
      return [];
    }
  };

  const getSelectedOptions = (card) => {
    const selectedOptions = {};

    card.querySelectorAll(selectors.optionControl).forEach((control) => {
      const position = control.dataset.optionPosition;

      if (!position) return;

      if (control.type === 'radio' && !control.checked) return;

      selectedOptions[`option${position}`] = control.value;
    });

    return selectedOptions;
  };

  const variantMatchesOptions = (variant, selectedOptions) => {
    return Object.entries(selectedOptions).every(([key, value]) => {
      return !value || variant[key] === value;
    });
  };

  const findVariant = (card) => {
    const variants = getVariants(card);
    const fallbackSelect = card.querySelector(selectors.fallbackSelect);

    if (fallbackSelect) {
      const selectedId = Number(fallbackSelect.value);
      return variants.find((variant) => Number(variant.id) === selectedId) || variants.find((variant) => variant.available);
    }

    const selectedOptions = getSelectedOptions(card);

    return (
      variants.find((variant) => variant.available && variantMatchesOptions(variant, selectedOptions)) ||
      variants.find((variant) => variantMatchesOptions(variant, selectedOptions)) ||
      variants.find((variant) => variant.available) ||
      variants[0]
    );
  };

  const updateSizeAvailability = (card) => {
    const variants = getVariants(card);
    const selectedOptions = getSelectedOptions(card);
    const sizeSelect = Array.from(card.querySelectorAll('select[data-ray-cross-sell-option]')).find((select) => {
      return select.dataset.optionPosition;
    });

    if (!sizeSelect) return;

    const sizePosition = sizeSelect.dataset.optionPosition;
    const optionsWithoutSize = { ...selectedOptions };

    delete optionsWithoutSize[`option${sizePosition}`];

    Array.from(sizeSelect.options).forEach((option) => {
      const testOptions = {
        ...optionsWithoutSize,
        [`option${sizePosition}`]: option.value,
      };

      const hasAvailableVariant = variants.some((variant) => {
        return variant.available && variantMatchesOptions(variant, testOptions);
      });

      option.disabled = !hasAvailableVariant;
    });

    if (sizeSelect.selectedOptions[0]?.disabled) {
      const firstAvailableOption = Array.from(sizeSelect.options).find((option) => !option.disabled);

      if (firstAvailableOption) {
        sizeSelect.value = firstAvailableOption.value;
      }
    }
  };

  const updateCard = (card) => {
    updateSizeAvailability(card);

    const variant = findVariant(card);

    if (!variant) return;

    const variantIdInput = card.querySelector(selectors.variantIdInput);
    const priceElement = card.querySelector(selectors.price);
    const comparePriceElement = card.querySelector(selectors.comparePrice);
    const imageElement = card.querySelector(selectors.image);
    const button = card.querySelector('button[type="submit"]');

    if (variantIdInput) {
      variantIdInput.value = variant.id;
    }

    if (priceElement && variant.price) {
      priceElement.textContent = variant.price;
    }

    if (comparePriceElement) {
      if (variant.compareAtPrice) {
        comparePriceElement.textContent = variant.compareAtPrice;
        comparePriceElement.hidden = false;
      } else {
        comparePriceElement.textContent = '';
        comparePriceElement.hidden = true;
      }
    }

    if (imageElement && variant.image) {
      imageElement.src = variant.image;
      imageElement.srcset = '';
    }

    if (button) {
      button.disabled = !variant.available;
    }
  };

  const initCards = (root = document) => {
    root.querySelectorAll(selectors.card).forEach((card) => {
      updateCard(card);
    });
  };

  const refreshHeaderCartParts = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const nextDrawerInner = doc.querySelector(selectors.drawerInner);
    const currentDrawerInner = document.querySelector(selectors.drawerInner);

    if (nextDrawerInner && currentDrawerInner) {
      currentDrawerInner.innerHTML = nextDrawerInner.innerHTML;
      initCards(currentDrawerInner);
    }

    const nextCartIcon = doc.querySelector(selectors.cartIcon);
    const currentCartIcon = document.querySelector(selectors.cartIcon);

    if (nextCartIcon && currentCartIcon) {
      currentCartIcon.innerHTML = nextCartIcon.innerHTML;
    }
  };

  const refreshCartDrawer = async (sectionId) => {
    if (!sectionId) return;

    const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`;

    const response = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('Cart drawer refresh failed');
    }

    const sections = await response.json();
    const html = sections[sectionId] || Object.values(sections)[0];

    if (!html) {
      throw new Error('Cart drawer section HTML not found');
    }

    refreshHeaderCartParts(html);
  };

  document.addEventListener('change', (event) => {
    const optionControl = event.target.closest(selectors.optionControl);
    const fallbackSelect = event.target.closest(selectors.fallbackSelect);

    if (!optionControl && !fallbackSelect) return;

    const card = event.target.closest(selectors.card);

    if (!card) return;

    updateCard(card);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest(selectors.form);

    if (!form) return;

    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const originalText = button ? button.textContent : '';

    try {
      if (button) {
        button.disabled = true;
        button.textContent = '...';
      }

      const formData = new FormData(form);

      const addResponse = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!addResponse.ok) {
        throw new Error('Cross-sell product add failed');
      }

      await refreshCartDrawer(form.dataset.sectionId);
    } catch (error) {
      console.error(error);

      if (button && button.isConnected) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCards());
  } else {
    initCards();
  }
})();